import type { FC } from 'react'

import { ChatFilled } from '@liveHelper/assets/ChatFilled'
import { ChatOutlined } from '@liveHelper/assets/ChatOutlined'
import { useMessagePreviewContext } from '@liveHelper/components/MessagePreviewProvider'
import { actionsSelector, useStore } from '@liveHelper/models/store'
import { CHAT_BUTTON_HIDE, CHAT_BUTTON_SHOW } from '@liveHelper/testIds'
import { ActionButton } from '@oneToOne/shared/styles'

import { useContextTranslation } from '@core/i18n'

import { ChatButtonBadge } from './ChatButton.styles'

interface ChatButtonProps {
  isMinimized: boolean
  chatEnabled: boolean
  showUnreadMessageNumber?: boolean
}

/**
 * ChatButton Component
 *
 * A button component for toggling chat to be visible or not. Currently this
 * is only used in mobileWeb version.
 */
export const ChatButton: FC<ChatButtonProps> = ({
  isMinimized,
  chatEnabled,
  showUnreadMessageNumber = false,
}) => {
  const { t } = useContextTranslation()
  const { sendUIStateTransitionEvent } = useStore(actionsSelector)
  const { lastUnreadMessage } = useMessagePreviewContext()

  return (
    <ActionButton
      data-testid={chatEnabled ? CHAT_BUTTON_HIDE : CHAT_BUTTON_SHOW}
      aria-label={
        chatEnabled
          ? t(`oneToOne:Hide Chat and expand video`)
          : t(`oneToOne:Show Chat and shrink video`)
      }
      buttonSize={isMinimized ? 'md' : 'lg'}
      actionType={'neutral'}
      shape="circle"
      onClick={() => {
        sendUIStateTransitionEvent('TOGGLE_CHAT')
      }}
      icon={
        chatEnabled ? (
          <ChatFilled
            width={isMinimized ? '18px' : '26px'}
            fill="var(--white)"
            label="chat"
          />
        ) : (
          <ChatOutlined
            width={isMinimized ? '18px' : '26px'}
            stroke="var(--white)"
            label="chat"
          />
        )
      }
    >
      {showUnreadMessageNumber && lastUnreadMessage?.numberOfUnreadMessages ? (
        <ChatButtonBadge>
          <span>{lastUnreadMessage?.numberOfUnreadMessages}</span>
        </ChatButtonBadge>
      ) : null}
    </ActionButton>
  )
}
